import React, { useState, useMemo, useEffect } from "react";
import { Box, Container } from "@mui/material";
import BackgroundImageCst from "@components/BackgroundImageCst";
import HeroCst from "@components/HeroCst";
import CostModelProjectFormCst from "../CostModelProjectFormCst";
import {
  useFetchDetail,
  usePostDetail,
  useFetchDataDetail,
} from "@hooks/useFetchDetail";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@components/Breadcrumb";
import { useTranslation } from "react-i18next";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import AnimationFadeIn from "@components/AnimationFadeIn";

const AddCostModelProject: React.FC = () => {
  const { idProject, id } = useParams();
  const { t } = useTranslation(["projects-cost-model-page"]);
  const navigate = useNavigate();
  const [options, setOptions] = useState<any[]>([]);

  const { columns } = useFetchDetail(
    `${ServicesURL.getCostCenters}?corporateId=${id}`,
    undefined,
    PagesID["projects.cost-model"],
  );

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const { trigger: triggerCostModelProject } = usePostDetail(
    `${ServicesURL.postCostModel.replace(":budgetProjectId", String(idProject))}?corporateId=${id}`,
  );

  /*Get the project information*/
  const { dataDetail: dataProject } = useFetchDataDetail(
    `${ServicesURL.getProjects}/${idProject}?corporateId=${id}`,
    PagesID["projects.cost-model"],
  );

  /*Get the account list for create the autocomplete options*/
  const { dataDetail: dataAccountList } = useFetchDataDetail(
    `${ServicesURL.getBankAccountListAll}?corporateId=${id}`,
    PagesID["projects.cost-model"],
  );

  /*Update the options for the autocomplete field*/
  useEffect(() => {
    if (!dataAccountList || !Array.isArray(dataAccountList)) {
      setOptions([]);
      return;
    }
    setOptions(
      dataAccountList.flatMap((item: any) =>
        item?.descriptions && Array.isArray(item.descriptions)
          ? item.descriptions.map((desc: any) => ({
              value: desc.id,
              label: `${item.sap_number} - ${desc.title}`,
            }))
          : [],
      ),
    );
  }, [dataAccountList]);

  /*Manage the columns for the form*/
  const getColumns = useMemo(() => {
    return columns.map((el) => {
      if (el.accessorKey === "account_description_id") {
        return { ...el, options: options || [] };
      }

      return { ...el };
    });
  }, [columns, options]);

  /*Define wich columns of the project are visibile like info*/
  const filteredDto = useMemo(() => {
    if (!dataProject?.dto) return null;

    return {
      code: dataProject.dto.code,
      title: dataProject.dto.title,
      cost_center: dataProject.dto.cost_center.sap_code,
      project_allocated_budget: dataProject.dto.allocated_budget,
      project_residual_budget: dataProject.dto.residual_budget,
    };
  }, [dataProject]);

  /*Submit of the form for create a new cost model*/
  const onSubmit = async (inputForm: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    const objToSend: any = {
      account_description_id: inputForm.account_description_id?.value || null,
      allocated_budget: inputForm.allocated_budget || null,
      type: inputForm.type || null,
    };

    try {
      await triggerCostModelProject(objToSend);
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setOpenToast(undefined);
      console.error(error);
    }
  };

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-cost-centers.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("add.hero-title")} description={""} />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <CostModelProjectFormCst
              row={filteredDto ? { ...filteredDto, type: "EXTERNAL" } : null}
              columns={getColumns}
              onSubmit={onSubmit}
              backButton={() => {
                navigate(
                  `/corporate/${id}/detail/budget-management/projects/edit/${idProject}`,
                  { state: { tab: "cost-model" } },
                );
              }}
              fullWidth={true}
            />
          </Box>
        )}
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={t("hero-title-add")}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default AddCostModelProject;
